import React from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import { PublicShell } from "@/public/PublicChrome";
import { Section, Reveal } from "@/public/ui";

// Real not-found page for unmatched public paths. Emits noindex,nofollow and a
// self-referential canonical (NOT the homepage canonical) so crawlers that render
// JS do not treat arbitrary URLs as the homepage. NOTE: the CRA static host still
// returns HTTP 200 for unmatched paths (platform constraint); this corrects the
// rendered indexing signals, which is the architecture-consistent fix.
export default function NotFound() {
  const location = useLocation();
  return (
    <PublicShell>
      <Seo
        title="Page not found"
        description="The page you're looking for doesn't exist. Explore TRADECRAFT market intelligence, the Analyst, or the glossary."
        path={location.pathname || "/404"}
        noindex={true}
        noCanonical={true}
      />
      <Section className="pt-40 pb-28 text-center">
        <div className="absolute inset-x-0 top-0 -z-10 h-[320px] bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.08),transparent_65%)]" aria-hidden="true" />
        <Reveal>
          <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-tc-muted">Error 404</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-tc-txt" data-testid="notfound-title">
            This page doesn't exist.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-tc-sub max-w-xl mx-auto">
            The address may be mistyped or the page may have moved. Here's where to go next.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="notfound-links">
            <Link to="/" className="rounded-xl bg-tc-coach text-tc-bg px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition">Home</Link>
            <Link to="/intelligence" className="rounded-xl border border-white/15 text-tc-txt px-5 py-2.5 text-sm font-semibold hover:border-tc-coach/50 transition">Intelligence</Link>
            <Link to="/insights" className="rounded-xl border border-white/15 text-tc-txt px-5 py-2.5 text-sm font-semibold hover:border-tc-coach/50 transition">Insights</Link>
            <Link to="/glossary" className="rounded-xl border border-white/15 text-tc-txt px-5 py-2.5 text-sm font-semibold hover:border-tc-coach/50 transition">Glossary</Link>
          </div>
        </Reveal>
      </Section>
    </PublicShell>
  );
}
